import { inject, Injectable } from '@angular/core';
import { catchError, map, Observable, of } from 'rxjs';

import { api } from '../../../models/api-url.model';
import { PaginatedResult } from '../../../models/paginated-result.model';
import { Ticket, TicketAggregate } from '../../../models/ticket/ticket.model';
// eslint-disable-next-line import/no-cycle -- ApiService is core; cycle via store/effects is accepted
import { ApiService } from '../api';

export const DEFAULT_TICKETS_TIME_RANGE = 'all';

@Injectable({
  providedIn: 'root',
})
export class TicketService {
  private readonly apiService = inject(ApiService);

  /**
   * Fetches a page of tickets using the current filters.
   */
  getTickets(
    page: number = 0,
    pageSize: number = 20,
    searchTerm: string = '',
    timeRange: string = DEFAULT_TICKETS_TIME_RANGE,
  ): Observable<PaginatedResult<Ticket>> {
    const params = { page, pageSize, searchTerm, timeRange };
    return this.apiService.get(api.ticketsURL, { params });
  }

  /**
   * Returns the number of open tickets in the default time range.
   */
  getOpenTicketTotal(): Observable<number> {
    return this.getTickets().pipe(
      map((results) => results.data?.filter((t) => !t.IsClosed).length ?? 0),
      catchError(() => of(0)), // fail-safe: return 0 on error
    );
  }

  /**
   * Returns aggregate ticket statistics for the optional time range filter.
   */
  getAggregateOpenTotal(timeRange?: string): Observable<TicketAggregate> {
    const params: Record<string, string> = {};
    if (timeRange) {
      params.timeRange = timeRange;
    }
    return this.apiService.get('/tickets/stats', { params });
  }

  /**
   * Creates a new NOC ticket with the provided payload.
   */
  createNocTicket(ticketData: Record<string, unknown>): Observable<unknown> {
    return this.apiService.post(`${api.ticketsURL}/noc`, ticketData);
  }

  /**
   * Creates a new Care ticket with the provided payload.
   */
  createCareTicket(ticketData: Record<string, unknown>): Observable<unknown> {
    return this.apiService.post(`${api.ticketsURL}/care`, ticketData);
  }

  /**
   * Creates a new Billing ticket with the provided payload.
   */
  createBillingTicket(
    ticketData: Record<string, unknown>,
  ): Observable<unknown> {
    return this.apiService.post(`${api.ticketsURL}/billing`, ticketData);
  }
}
